import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * "Applications" (`/app-application`) — third-party applications that group resources
 * (agents, connections, embed keys, API tokens) per integration. The list data comes
 * from the bespoke GET /api/applications endpoint which enriches each row with resource
 * counts in a single round-trip.
 */
function AppStatusBadge({ app }) {
  if (!app.is_active) return <Badge variant="secondary" size="sm">inactive</Badge>;
  return <Badge variant="success" size="sm">active</Badge>;
}

function appColumns(nav) {
  return [
    {
      key: 'name',
      label: 'Name',
      render: (a) => (
        <div>
          <button onClick={() => nav(`/app-application/${a.id}`)} className="font-medium text-primary-600 hover:underline text-left">
            {a.name}
          </button>
          <div className="text-secondary-500 text-xs max-w-xs truncate" title={a.description}>{a.description}</div>
        </div>
      ),
    },
    { key: 'key', label: 'Key', render: (a) => <span className="font-mono text-xs">{a.key}</span> },
    {
      key: 'agent_count',
      label: 'Agents',
      searchable: false,
      sortable: true,
      render: (a) => <span className="font-mono text-xs">{a.agent_count}</span>,
    },
    {
      key: 'embed_key_count',
      label: 'Embed Keys',
      searchable: false,
      sortable: true,
      render: (a) => <span className="font-mono text-xs">{a.embed_key_count}</span>,
    },
    {
      key: 'api_token_count',
      label: 'API Tokens',
      searchable: false,
      sortable: true,
      render: (a) => <span className="font-mono text-xs">{a.api_token_count}</span>,
    },
    {
      key: 'is_active',
      label: 'Status',
      searchable: false,
      render: (a) => <AppStatusBadge app={a} />,
    },
  ];
}

export default function ApplicationsPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const reload = useCallback(() => {
    setLoading(true);
    return fetchApi('applicationsList')
      .then((res) => setApps((Array.isArray(res) ? res : res?.data) || []))
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const columns = useMemo(() => appColumns(nav), [nav]);

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Applications"
        subtitle={(
          <>
            {apps.length} application{apps.length === 1 ? '' : 's'} — group agents, connections and keys per integration.
          </>
        )}
        actions={(
          <Can module="applications" action="govern">
            <Button size="sm" onClick={() => nav('/app-application/new')}>
              New application
            </Button>
          </Can>
        )}
      />

      <DataTable
        data={apps}
        columns={columns}
        loading={loading}
        error={err}
        emptyText="No applications yet."
        search={{ placeholder: 'Search applications…', keys: ['name', 'key', 'description'] }}
        onReload={reload}
      />
    </PageShell>
  );
}
